import { useEffect, useRef, useState } from 'react';

// Hosting serves index.html with no-cache, but nothing tells an open tab that a deploy happened:
// there is no service worker, so a tab left open keeps running the bundle it loaded (its Firestore
// queries, its pricing code) until someone reloads it. This hook polls index.html and compares the
// main bundle it names with the one this page loaded. Once they differ it reports an update, and
// after IDLE_RELOAD_MS with no user activity it reloads the page itself.

export const VERSION_POLL_MS = 5 * 60 * 1000;
export const IDLE_RELOAD_MS = 30 * 60 * 1000;
const IDLE_CHECK_MS = 60 * 1000;
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'wheel', 'scroll'];

// CRA names the entry bundle static/js/main.<contenthash>.js. The dev server serves an unhashed
// bundle.js instead, so there is nothing to compare and the hook stays idle.
const MAIN_BUNDLE_SRC = /\/static\/js\/(main\.[0-9a-f]+\.js)$/;
const MAIN_BUNDLE_TAG = /<script\b[^>]*\bsrc="[^"]*\/static\/js\/(main\.[0-9a-f]+\.js)"/;

const loadedMainBundle = () => {
    for (const script of Array.from(document.scripts)) {
        const match = MAIN_BUNDLE_SRC.exec(script.getAttribute('src') || '');
        if (match) return match[1];
    }
    return null;
};

const reloadPage = () => window.location.reload();

export default function useAppVersionCheck({ reload = reloadPage } = {}) {
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const reloadRef = useRef(reload);
    reloadRef.current = reload;

    useEffect(() => {
        if (updateAvailable) return undefined;
        const loaded = loadedMainBundle();
        if (!loaded) return undefined;

        let cancelled = false;
        let lastCheck = Date.now();
        const check = async () => {
            lastCheck = Date.now();
            try {
                const res = await fetch('/index.html', { cache: 'no-store' });
                if (!res.ok) return;
                const match = MAIN_BUNDLE_TAG.exec(await res.text());
                if (!cancelled && match && match[1] !== loaded) setUpdateAvailable(true);
            } catch (_) {
                // Offline or mid-deploy: the next poll tries again.
            }
        };
        // Background tabs throttle timers, so also check on return if a poll is overdue.
        const onVisible = () => {
            if (document.visibilityState === 'visible' && Date.now() - lastCheck >= VERSION_POLL_MS) check();
        };

        const timer = setInterval(check, VERSION_POLL_MS);
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            cancelled = true;
            clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [updateAvailable]);

    useEffect(() => {
        if (!updateAvailable) return undefined;

        // The idle window starts when the notice appears, so it is on screen for at least
        // IDLE_RELOAD_MS before the page reloads itself.
        let lastActivity = Date.now();
        const markActive = () => { lastActivity = Date.now(); };
        const listenerOptions = { capture: true, passive: true };
        ACTIVITY_EVENTS.forEach((type) => window.addEventListener(type, markActive, listenerOptions));
        const timer = setInterval(() => {
            if (Date.now() - lastActivity < IDLE_RELOAD_MS) return;
            clearInterval(timer);
            reloadRef.current();
        }, IDLE_CHECK_MS);
        return () => {
            clearInterval(timer);
            ACTIVITY_EVENTS.forEach((type) => window.removeEventListener(type, markActive, listenerOptions));
        };
    }, [updateAvailable]);

    return { updateAvailable, reload: () => reloadRef.current() };
}
